import { CheckedSquareIcon, SquareIcon } from '@va/icons';
import { DISABLED_CLASSES, fontWeights, Paragraph, paragraphSizes, SECONDARY_CLASSES } from '@va/ui/design-system';
import { TooltipWrapper, useTooltipContext } from '@va/ui/tooltips';
import { getResponsiveTooltipHeight } from '@va/util/helpers';
import { useWindowDimensions } from '@va/util/hooks';
import { useIsolatedFocusedContainer } from '@va/util/misc';
import classNames from 'classnames';
import { isNil } from 'lodash';
import React, { ReactNode, useCallback, useRef } from 'react';

export type SelectActionDropdownOption = {
  onClick: () => void;
  icon?: () => ReactNode;
  text: string;
  value?: number;
  textColorClass?: string;
  disabled?: boolean;
  tooltip?: string;
  hideDropdownOnClick?: boolean;
  tooltipProps?: {
    visible: boolean;
  };
  'data-testid'?: string;
  hidden?: boolean;
};

type SelectActionDropdownContentProps = {
  options: SelectActionDropdownOption[];
  hideDropdown: () => void;
  selectedOption?: number | null;
  focusButton?: () => void;
};

export const SelectActionDropdownContent: React.FC<SelectActionDropdownContentProps> = ({
  options,
  hideDropdown,
  selectedOption,
  focusButton,
}) => {
  const { context } = useTooltipContext();
  const containerRef = useRef<HTMLDivElement>(null);
  const { height: windowHeight } = useWindowDimensions();

  useIsolatedFocusedContainer({ containerRef, enabled: context.open });

  const getOptionIcon = useCallback(
    (index: number, icon?: () => ReactNode, value?: number) => {
      if (icon) return icon();

      if ((!isNil(value) && selectedOption === value) || selectedOption === index) {
        return <CheckedSquareIcon color='#696969' />;
      }

      return <SquareIcon color='#696969' />;
    },
    [selectedOption],
  );

  return (
    <div ref={containerRef} className='flex rounded-12 flex-col bg-white p-6px text-gray-charcoal min-w-78'>
      <div
        className='overflow-auto scrollbar scrollbar-thumb'
        style={{ maxHeight: getResponsiveTooltipHeight(windowHeight) }}
      >
        {options.map(
          (
            {
              onClick,
              hideDropdownOnClick = true,
              text,
              value,
              tooltipProps,
              icon,
              textColorClass,
              tooltip,
              disabled = false,
              'data-testid': dataTestId,
              hidden = false,
            },
            index,
          ) => {
            if (hidden) return null;

            return (
              <TooltipWrapper key={index} content={tooltip} disabled={!tooltip} open={tooltipProps?.visible}>
                <button
                  onClick={() => {
                    if (disabled) return;
                    if (hideDropdownOnClick) {
                      hideDropdown();
                      focusButton?.();
                    }
                    onClick();
                  }}
                  data-testid={dataTestId}
                  className={classNames(
                    'flex w-full gap-3 rounded-12 h-12 items-center px-3 hover:bg-white-snow min-w-150 transition-colors duration-300',
                    disabled && DISABLED_CLASSES,
                    (selectedOption === index || (!isNil(value) && selectedOption === value)) && SECONDARY_CLASSES,
                  )}
                >
                  {icon ? getOptionIcon(index, icon, value) : getOptionIcon(index, undefined, value)}
                  <Paragraph
                    colorClassName={disabled ? undefined : textColorClass}
                    weight={fontWeights.medium}
                    size={paragraphSizes.tiny}
                  >
                    {text}
                  </Paragraph>
                </button>
              </TooltipWrapper>
            );
          },
        )}
      </div>
    </div>
  );
};
